/**
 * Tiny CSV reader for finance imports (Sep 14, 2026). RFC-4180 quoting
 * (embedded delimiters/newlines, doubled quotes), CR / LF / CRLF endings, a
 * UTF-8 BOM, and a one-line delimiter sniff (comma / semicolon / tab).
 * No dependency on purpose — the RoofHub payment-history export is 11 columns
 * × ~50 rows; a parsing library would be 20 KB of bundle for that.
 */

export function sniffDelimiter(firstLine: string): string {
  const counts: Array<[string, number]> = [",", ";", "\t"].map((d) => [d, firstLine.split(d).length - 1]);
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ",";
}

export function parseCsv(text: string, delimiter?: string): string[][] {
  const s = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const d = delimiter ?? sniffDelimiter(s.split(/\r\n|\r|\n/, 1)[0] ?? "");
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') { inQuotes = true; continue; }
    if (c === d) { row.push(field); field = ""; continue; }
    if (c === "\r" || c === "\n") {
      row.push(field); rows.push(row); row = []; field = "";
      if (c === "\r" && s[i + 1] === "\n") i++;
      continue;
    }
    field += c;
  }
  if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
  // drop blank rows (trailing newline, spacer lines)
  return rows.filter((r) => r.some((f) => f.trim() !== ""));
}

/** "$1,234.56" → 1234.56 · "(827.61)" → -827.61 · "-827.61" → -827.61 · "" → null. Rounded to cents. */
export function parseMoney(v: string | null | undefined): number | null {
  if (v == null) return null;
  let t = v.trim();
  if (!t) return null;
  let neg = false;
  if (/^\(.*\)$/.test(t)) { neg = true; t = t.slice(1, -1); }
  if (t.endsWith("-")) { neg = true; t = t.slice(0, -1); }
  t = t.replace(/[$,\s]/g, "");
  if (t.startsWith("-")) { neg = true; t = t.slice(1); }
  if (!/^\d*\.?\d+$/.test(t)) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  const r = Math.round(n * 100) / 100;
  return neg ? -r : r;
}

/** MM/DD/YYYY · M/D/YY · YYYY-MM-DD · YYYY/MM/DD · MM-DD-YYYY · ISO datetime → "YYYY-MM-DD" (or null). */
export function parseDateISO(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = v.trim();
  if (!t) return null;
  let m = t.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) return isoOrNull(m[1], m[2], m[3]);
  m = t.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/);
  if (m) return isoOrNull(m[3].length === 2 ? `20${m[3]}` : m[3], m[1], m[2]);
  return null;
}

function isoOrNull(y: string, mo: string, d: string): string | null {
  const Y = Number(y), M = Number(mo), D = Number(d);
  if (!(Y >= 2000 && Y <= 2100 && M >= 1 && M <= 12 && D >= 1 && D <= 31)) return null;
  return `${Y}-${String(M).padStart(2, "0")}-${String(D).padStart(2, "0")}`;
}
